import React, { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
import { AuthClient } from "../api/AuthClient";
import { AuthContext, type AuthContextValue } from "./AuthContext";
import { LoadingScreen } from "../components/LoadingScreen";
import { SessionExpiredDialog, type SessionExpiredDialogProps } from "../components/SessionExpiredDialog";
import type { AuthProvider as IAuthProvider, User } from "../types";

export interface AuthProviderProps {
  /** A configured provider instance — e.g. `new JwtProvider({ baseUrl: "/api/auth" })`. */
  provider: IAuthProvider;
  /** Base URL for the host app's own API, used by `auth.client.request(...)`. */
  apiBaseUrl?: string;
  /** Where "Login Again" in the session-expired dialog should navigate. Default "/login". */
  loginPath?: string;
  /** Suppress the automatic full-screen loading state while the session is restored. */
  hideLoadingScreen?: boolean;
  /** Suppress the automatic session-expired dialog (handle the `sessionExpired` event yourself instead). */
  hideSessionExpiredDialog?: boolean;
  /** Swap the default LoadingScreen without modifying the library. */
  loadingScreenComponent?: ComponentType;
  /** Swap the default SessionExpiredDialog without modifying the library. */
  sessionExpiredDialogComponent?: ComponentType<SessionExpiredDialogProps>;
  children: ReactNode;
}

/**
 * Wire a configured AuthProvider (JwtProvider, OAuthProvider, KeycloakProvider,
 * Auth0Provider, AzureProvider, or a custom implementation) into the React tree.
 *
 * ```tsx
 * <AuthProvider provider={new JwtProvider({ baseUrl: "/api/auth", storage: new LocalStorageTokenStorage() })}>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({
  provider,
  apiBaseUrl,
  loginPath = "/login",
  hideLoadingScreen,
  hideSessionExpiredDialog,
  loadingScreenComponent: LoadingComponent = LoadingScreen,
  sessionExpiredDialogComponent: ExpiredDialog = SessionExpiredDialog,
  children,
}: AuthProviderProps) {
  const client = useMemo(() => new AuthClient({ provider, apiBaseUrl }), [provider, apiBaseUrl]);

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    let cancelled = false;

    const unsubLogin = client.events.on("login", ({ user }) => !cancelled && setUser(user));
    const unsubLogout = client.events.on("logout", () => !cancelled && setUser(null));
    const unsubUserChanged = client.events.on("userChanged", ({ user }) => !cancelled && setUser(user));
    const unsubExpired = client.events.on("sessionExpired", () => !cancelled && setSessionExpired(true));

    client
      .initialize()
      .then(() =>  client.getCurrentUser())
      .then((current) => !cancelled && setUser(current))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
      unsubLogin();
      unsubLogout();
      unsubUserChanged();
      unsubExpired();
      // Only dispose providers that expose it (BaseAuthProvider-derived ones do).
      const disposable = provider as Partial<{ dispose: () => void }>;
      if (!disposedRef.current && typeof disposable.dispose === "function") {
        disposable.dispose();
        disposedRef.current = true;
      }
    };
  }, [client, provider]);

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      loading,
      authenticated: client.isAuthenticated() && user !== null,
      login: (credentials) => client.login(credentials),
      logout: () => client.logout(),
      refresh: () => client.refresh(),
      client: client.client,
      events: client.events,
    }),
    [user, loading, client]
  );

  if (loading && !hideLoadingScreen) {
    return <LoadingComponent />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      {sessionExpired && !hideSessionExpiredDialog && (
        <ExpiredDialog
          onLoginAgain={async () => {
            setSessionExpired(false);
            await client.logout().catch(() => undefined);
            if (typeof window !== "undefined") window.location.assign(loginPath);
          }}
          onLogout={async () => {
            setSessionExpired(false);
            await client.logout().catch(() => undefined);
          }}
        />
      )}
    </AuthContext.Provider>
  );
}
